'use client'

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdDashboard,
    MdBed,
    MdPerson,
    MdExitToApp,
    MdWarning,
    MdSchedule,
    MdDescription,
    MdRefresh,
    MdArrowForward,
} from 'react-icons/md'
import { useSelector } from 'react-redux'
import { RootState } from '@/store'
import {
    getDashboardOverview,
    getOccupancyByUnit,
    getPendingChecks,
    getPendingEvolutions,
    getCriticalAlerts,
    DashboardOverview,
    UnitOccupancy,
    PendingCheck,
    PendingEvolution,
    CriticalAlert,
} from '@/lib/api/hospital'
import { HospitalSelector } from '@/components/HospitalSelector'
import { useHospitals } from '@/hooks/useHospitals'

export default function HospitalDashboardPage() {
    const navigate = useNavigate()
    const company = useSelector((state: RootState) => state.auth.company)
    const companyId = company?.id || 0
    const {
        hospitals,
        loading: loadingHospitals,
        selectedHospitalId,
        setSelected,
    } = useHospitals()

    const [loading, setLoading] = useState(true)
    const [overview, setOverview] = useState<DashboardOverview | null>(null)
    const [occupancy, setOccupancy] = useState<UnitOccupancy[]>([])
    const [pendingChecks, setPendingChecks] = useState<PendingCheck[]>([])
    const [pendingEvolutions, setPendingEvolutions] = useState<
        PendingEvolution[]
    >([])
    const [criticalAlerts, setCriticalAlerts] = useState<CriticalAlert[]>([])

    useEffect(() => {
        if (companyId) {
            loadDashboard()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, selectedHospitalId])

    const loadDashboard = async () => {
        setLoading(true)
        const hId = selectedHospitalId || undefined
        try {
            const [
                overviewRes,
                occupancyRes,
                checksRes,
                evolutionsRes,
                alertsRes,
            ] = await Promise.all([
                getDashboardOverview(companyId, hId),
                getOccupancyByUnit(companyId, hId),
                getPendingChecks(companyId, hId),
                getPendingEvolutions(companyId, hId),
                getCriticalAlerts(companyId, hId),
            ])

            if (overviewRes.success) setOverview(overviewRes.result)
            if (occupancyRes.success) setOccupancy(occupancyRes.result)
            if (checksRes.success) setPendingChecks(checksRes.result)
            if (evolutionsRes.success)
                setPendingEvolutions(evolutionsRes.result)
            if (alertsRes.success) setCriticalAlerts(alertsRes.result)
        } catch (error) {
            toast.error('Erro ao carregar dashboard')
        } finally {
            setLoading(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando dashboard...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdDashboard className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            Dashboard Hospitalar
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Visão geral das internações e ocupação
                        </p>
                    </div>
                </div>
                <button onClick={loadDashboard} className="btn btn-ghost gap-2">
                    <MdRefresh className="text-xl" />
                    Atualizar
                </button>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <p className="text-sm text-base-content/60 mb-2">Hospital</p>
                <HospitalSelector
                    hospitals={hospitals}
                    selectedId={selectedHospitalId}
                    onChange={setSelected}
                    loading={loadingHospitals}
                />
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="stat bg-base-100 rounded-xl border border-base-300/50 shadow-sm">
                    <div className="stat-figure text-secondary">
                        <MdBed className="text-3xl" />
                    </div>
                    <div className="stat-title">Total de Leitos</div>
                    <div className="stat-value text-secondary">
                        {overview?.beds.total || 0}
                    </div>
                    <div className="stat-desc">
                        {overview?.beds.available || 0} disponíveis
                    </div>
                </div>

                <div className="stat bg-base-100 rounded-xl border border-base-300/50 shadow-sm">
                    <div className="stat-figure text-success">
                        <MdPerson className="text-3xl" />
                    </div>
                    <div className="stat-title">Internações Ativas</div>
                    <div className="stat-value text-success">
                        {overview?.admissions.active || 0}
                    </div>
                    <div className="stat-desc">
                        {overview?.admissions.today || 0} hoje
                    </div>
                </div>

                <div className="stat bg-base-100 rounded-xl border border-base-300/50 shadow-sm">
                    <div className="stat-figure text-info">
                        <MdExitToApp className="text-3xl" />
                    </div>
                    <div className="stat-title">Altas Hoje</div>
                    <div className="stat-value text-info">
                        {overview?.discharges.today || 0}
                    </div>
                    <div className="stat-desc">
                        {overview?.discharges.pending || 0} pendentes
                    </div>
                </div>

                <div className="stat bg-base-100 rounded-xl border border-base-300/50 shadow-sm">
                    <div className="stat-figure text-warning">
                        <div
                            className="radial-progress text-warning"
                            style={
                                {
                                    '--value':
                                        overview?.beds.occupancyRate || 0,
                                    '--size': '3rem',
                                } as any
                            }
                        >
                            <span className="text-xs font-bold">
                                {overview?.beds.occupancyRate || 0}%
                            </span>
                        </div>
                    </div>
                    <div className="stat-title">Taxa de Ocupação</div>
                    <div className="stat-value text-warning">
                        {overview?.beds.occupied || 0}
                    </div>
                    <div className="stat-desc">leitos ocupados</div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Critical Alerts */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <MdWarning className="text-xl text-error" />
                            <h3 className="font-bold">Alertas Críticos</h3>
                        </div>
                        <span className="badge badge-error">
                            {criticalAlerts.length}
                        </span>
                    </div>
                    {criticalAlerts.length === 0 ? (
                        <p className="text-center text-base-content/60 py-4">
                            Nenhum alerta crítico
                        </p>
                    ) : (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                            {criticalAlerts.map(alert => (
                                <div
                                    key={alert.id}
                                    className="flex items-start gap-3 p-2 bg-error/10 rounded-lg"
                                >
                                    <MdWarning className="text-error text-lg mt-0.5" />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-sm">
                                            {alert.admission?.client.name}
                                        </p>
                                        <p className="text-xs text-base-content/70">
                                            {alert.message}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Pending Checks */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <MdSchedule className="text-xl text-warning" />
                            <h3 className="font-bold">Checagens Pendentes</h3>
                        </div>
                        <button
                            onClick={() => navigate('/hospital/prescricoes')}
                            className="btn btn-ghost btn-xs gap-1"
                        >
                            Ver todas
                            <MdArrowForward />
                        </button>
                    </div>
                    {pendingChecks.length === 0 ? (
                        <p className="text-center text-base-content/60 py-4">
                            Nenhuma checagem pendente
                        </p>
                    ) : (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                            {pendingChecks.slice(0, 5).map(check => (
                                <div
                                    key={check.id}
                                    className="flex items-center justify-between p-2 bg-base-200/50 rounded-lg"
                                >
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-sm truncate">
                                            {check.prescription.client.name}
                                        </p>
                                        <p className="text-xs text-base-content/70">
                                            {check.item.medicationName} -{' '}
                                            {check.item.dose}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm font-medium">
                                            {new Date(
                                                check.scheduledTime
                                            ).toLocaleTimeString('pt-BR', {
                                                hour: '2-digit',
                                                minute: '2-digit',
                                            })}
                                        </p>
                                        <p className="text-xs text-base-content/60">
                                            {check.prescription.admission.bed
                                                ?.number || '-'}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Occupancy by Unit */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <MdBed className="text-xl text-secondary" />
                            <h3 className="font-bold">Ocupação por Unidade</h3>
                        </div>
                        <button
                            onClick={() => navigate('/hospital/unidades')}
                            className="btn btn-ghost btn-xs gap-1"
                        >
                            Ver todas
                            <MdArrowForward />
                        </button>
                    </div>
                    {occupancy.length === 0 ? (
                        <p className="text-center text-base-content/60 py-4">
                            Nenhuma unidade cadastrada
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {occupancy.map(unit => (
                                <div key={unit.unitId} className="space-y-1">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium">
                                            {unit.unitName}
                                        </span>
                                        <span className="text-sm text-base-content/70">
                                            {unit.occupiedBeds}/{unit.totalBeds}{' '}
                                            leitos
                                        </span>
                                    </div>
                                    <progress
                                        className={`progress w-full ${
                                            unit.occupancyRate > 90
                                                ? 'progress-error'
                                                : unit.occupancyRate > 70
                                                  ? 'progress-warning'
                                                  : 'progress-success'
                                        }`}
                                        value={unit.occupancyRate}
                                        max="100"
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Pending Evolutions */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <MdDescription className="text-xl text-info" />
                            <h3 className="font-bold">Evoluções Pendentes</h3>
                        </div>
                        <button
                            onClick={() => navigate('/hospital/evolucoes')}
                            className="btn btn-ghost btn-xs gap-1"
                        >
                            Ver todas
                            <MdArrowForward />
                        </button>
                    </div>
                    {pendingEvolutions.length === 0 ? (
                        <p className="text-center text-base-content/60 py-4">
                            Todas as evoluções em dia
                        </p>
                    ) : (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                            {pendingEvolutions.slice(0, 5).map(evolution => (
                                <div
                                    key={evolution.id}
                                    className="flex items-center justify-between p-2 bg-base-200/50 rounded-lg cursor-pointer hover:bg-base-200"
                                    onClick={() =>
                                        navigate(
                                            `/hospital/evolucoes/new?admissionId=${evolution.id}`
                                        )
                                    }
                                >
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-sm truncate">
                                            {evolution.client.name}
                                        </p>
                                        <p className="text-xs text-base-content/70">
                                            {evolution.unit.name} - Leito{' '}
                                            {evolution.bed?.number || '-'}
                                        </p>
                                    </div>
                                    <MdArrowForward className="text-base-content/50" />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
